import * as React from "react";
import { useMemo } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { PresetResponse, presetsApi } from "@/lib/presets";
import { lightPresetGroupsApi } from "@/lib/light-preset-groups";
import { AreaResponse } from "@/lib/areas";
import type { CurfewConfig } from "@/lib/weekly-availability";
import { useAuth } from "@/hooks/useAuth";
import LightPresetAvailabilityCell from "@/components/scheduling/LightPresetAvailabilityCell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type PresetsByArea = Map<
  string,
  { area: AreaResponse; presets: PresetResponse[] }
>;

type BaseProps = {
  siteId: string;
  lightPresets: PresetResponse[] | undefined;
  presetsByArea: PresetsByArea;
  groupIdForRelations: string | undefined;
  /** Site curfew for “All” (full non-curfew) comparison; optional. */
  siteCurfew?: CurfewConfig;
  cardClassName?: string;
};

export type GroupLightingPresetsListReadonlyProps = BaseProps & {
  variant: "readonly";
  selectedPresetIds: string[];
  /** Called when user clicks the readonly list to switch into edit mode. */
  onRequestEdit: () => void;
  onUiAlert: (args: { title: string; message: string }) => void;
  onOpenAvailabilityEditor: (args: {
    preset: PresetResponse;
    lightPresetGroupId: string;
  }) => void;
};

export type GroupLightingPresetsListEditProps = BaseProps & {
  variant: "edit";
  draftPresetIds: string[];
  /** True when editing an existing group (vs creating a new one). */
  isEditingGroup: boolean;
  isSubmitting: boolean;
  disableEdits: boolean;
  onTogglePreset: (presetId: string) => void;
  onToggleArea: (areaPresets: PresetResponse[]) => void;
  onOpenAvailabilityEditor: (args: {
    preset: PresetResponse;
    lightPresetGroupId: string;
  }) => void;
  onUiAlert: (args: { title: string; message: string }) => void;
  onApplySelectionForCreate: () => void;
  onRequestClose: () => void;
};

export type GroupLightingPresetsListProps =
  | GroupLightingPresetsListReadonlyProps
  | GroupLightingPresetsListEditProps;

function emptyStateMessage(
  siteId: string,
  lightPresets: PresetResponse[] | undefined,
): string {
  if (!siteId) return "Please select a site first";
  if (lightPresets === undefined) return "Loading presets...";
  return "No presets available for this site";
}

/** Shared accordion + tables layout for readonly (disabled) and edit modes. */
function EditBody(props: GroupLightingPresetsListProps): React.ReactNode {
  const { siteId, lightPresets, presetsByArea, groupIdForRelations } = props;
  const siteCurfew = props.siteCurfew ?? {
    start: undefined,
    end: undefined,
  };
  const readonly = props.variant === "readonly";
  const selectedIds = readonly ? props.selectedPresetIds : props.draftPresetIds;
  const readonlySelectedIds =
    props.variant === "readonly" ? props.selectedPresetIds : undefined;
  const disableEdits = !readonly && props.isEditingGroup && props.disableEdits;
  const showPencil = !readonly && props.variant === "edit";
  const onRequestEdit = readonly ? props.onRequestEdit : undefined;

  const displayPresetsByArea = useMemo((): PresetsByArea => {
    if (!readonlySelectedIds) return presetsByArea;
    const selected = new Set(readonlySelectedIds);
    const out: PresetsByArea = new Map();
    for (const [areaId, { area, presets }] of presetsByArea) {
      const filtered = presets.filter((p) => selected.has(p.id));
      if (filtered.length > 0) {
        out.set(areaId, { area, presets: filtered });
      }
    }
    return out;
  }, [presetsByArea, readonlySelectedIds]);

  const { user } = useAuth();
  const canLoadSchedule =
    user?.role === "super_user" || user?.role === "site_admin";
  const queryClient = useQueryClient();

  const patchOverrideMutation = useMutation({
    mutationFn: ({
      lightPresetGroupId,
      overridePresetAvailableTimes,
    }: {
      lightPresetGroupId: string;
      overridePresetAvailableTimes: boolean;
    }) =>
      lightPresetGroupsApi.patch(lightPresetGroupId, {
        overridePresetAvailableTimes,
      }),
    onSuccess: async (_, { lightPresetGroupId }) => {
      await queryClient.invalidateQueries({ queryKey: ["light-presets"] });
      await queryClient.invalidateQueries({
        queryKey: ["light-preset-group", lightPresetGroupId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["light-preset-group-available-times", lightPresetGroupId],
      });
      await queryClient.invalidateQueries({ queryKey: ["preset-available-times"] });
    },
    onError: (error: unknown) => {
      const e = error as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      props.onUiAlert({
        title: "Error",
        message:
          e.response?.data?.error ??
          e.message ??
          "Could not update override setting",
      });
    },
  });

  const flatPresets = useMemo(() => {
    const list: { preset: PresetResponse; area: AreaResponse }[] = [];
    for (const [, { area, presets }] of displayPresetsByArea) {
      for (const p of presets) {
        if (p.type === "light") list.push({ preset: p, area });
      }
    }
    return list;
  }, [displayPresetsByArea]);

  const availabilityQueries = useQueries({
    queries: flatPresets.map(({ preset }) => {
      const rel = preset.groups?.find((g) => g.groupId === groupIdForRelations);
      const lpgId = rel?.lightPresetGroupId ?? undefined;
      const useGroupWindows =
        lpgId != null && (rel?.overridePresetAvailableTimes ?? true);
      return {
        queryKey: useGroupWindows
          ? (["light-preset-group-available-times", lpgId] as const)
          : (["preset-available-times", preset.id] as const),
        queryFn: () =>
          useGroupWindows
            ? lightPresetGroupsApi.getAvailableTimes(lpgId!)
            : presetsApi.getAvailableTimes(preset.id),
        enabled:
          canLoadSchedule &&
          flatPresets.length > 0 &&
          selectedIds.includes(preset.id),
        staleTime: 60_000,
      };
    }),
  });

  const availabilityByPresetId = useMemo(() => {
    const m = new Map<
      string,
      | { state: "pending" }
      | { state: "error" }
      | {
          state: "ok";
          rows: { weekday: number; startTime: string; endTime: string }[];
        }
    >();
    flatPresets.forEach(({ preset }, i) => {
      const q = availabilityQueries[i];
      if (!q) m.set(preset.id, { state: "pending" });
      else if (q.isPending) m.set(preset.id, { state: "pending" });
      else if (q.isError) m.set(preset.id, { state: "error" });
      else m.set(preset.id, { state: "ok", rows: q.data ?? [] });
    });
    return m;
  }, [flatPresets, availabilityQueries]);

  if (lightPresets === undefined) {
    return (
      <p className="text-sm text-muted-foreground py-2">
        {siteId ? "Loading presets..." : "Please select a site first"}
      </p>
    );
  }

  if (readonly && selectedIds.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-2">
        No lighting presets selected
      </p>
    );
  }

  if (lightPresets.length === 0 || displayPresetsByArea.size === 0) {
    return (
      <p className="text-sm text-muted-foreground py-2">
        {emptyStateMessage(siteId, lightPresets)}
      </p>
    );
  }

  const onTogglePreset = (presetId: string) => {
    if (readonly) return;
    props.onTogglePreset(presetId);
  };

  const onToggleArea = (areaPresets: PresetResponse[]) => {
    if (readonly) return;
    props.onToggleArea(areaPresets);
  };

  const isEditExistingGroup =
    props.variant === "edit" && props.isEditingGroup;
  const canEditOverrideCheckbox =
    isEditExistingGroup &&
    canLoadSchedule &&
    !disableEdits &&
    !patchOverrideMutation.isPending;

  const defaultExpanded = Array.from(displayPresetsByArea.keys());

  return (
    <Accordion
      type="multiple"
      className="w-full"
      defaultValue={defaultExpanded}
    >
      {Array.from(displayPresetsByArea.entries()).map(
        ([areaId, { area, presets: areaPresets }]) => {
          const selectedCount = areaPresets.filter((p) =>
            selectedIds.includes(p.id),
          ).length;
          const allSelected =
            selectedCount === areaPresets.length && areaPresets.length > 0;

          return (
            <AccordionItem key={areaId} value={areaId}>
              <div className="flex items-center space-x-2 border-b">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() => onToggleArea(areaPresets)}
                  className="ml-4"
                  onClick={(e) => e.stopPropagation()}
                  disabled={readonly || disableEdits}
                />
                <AccordionTrigger className="flex-1 text-sm hover:no-underline">
                  <div className="flex items-center w-full pr-2 gap-2">
                    <span className="font-medium flex-1 min-w-0 truncate">
                      {area.name}
                    </span>
                    {!readonly && selectedCount > 0 && (
                      <span className="text-xs text-muted-foreground text-right min-w-fit">
                        {selectedCount} of {areaPresets.length} selected
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
              </div>
              <AccordionContent>
                <div className="pt-2">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10 text-center"></TableHead>
                          <TableHead className="text-left">Preset</TableHead>
                          <TableHead className="hidden sm:table-cell min-w-[6rem] max-w-[12rem] text-left">
                            Alias
                          </TableHead>
                          <TableHead className="hidden sm:table-cell w-24">
                            Address
                          </TableHead>
                          <TableHead
                            className="hidden md:table-cell w-14 text-center"
                            title="Use this group’s weekly windows instead of the preset’s schedule"
                          >
                            Override
                          </TableHead>
                          <TableHead className="hidden md:table-cell min-w-[8rem] text-center">
                            Availability
                          </TableHead>
                          {showPencil && (
                            <TableHead className="w-12 text-center"></TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {areaPresets.map((preset) => {
                          const selected = selectedIds.includes(preset.id);
                          const rel = preset.groups?.find(
                            (g) => g.groupId === groupIdForRelations,
                          );
                          const aliasText =
                            rel?.lightPresetGroupId != null
                              ? rel.alias?.trim() || "—"
                              : "—";
                          const avail = availabilityByPresetId.get(preset.id);
                          const areaCurfew = {
                            start: area.curfewStart,
                            end: area.curfewEnd,
                          };
                          return (
                            <TableRow
                              key={preset.id}
                              className={
                                readonly
                                  ? "hover:bg-accent/40 cursor-pointer"
                                  : "hover:bg-accent/40"
                              }
                              onClick={() => {
                                if (readonly) onRequestEdit?.();
                              }}
                            >
                              <TableCell className="p-2 align-middle text-center">
                                <div className="flex justify-center">
                                  <Checkbox
                                    checked={selected}
                                    onCheckedChange={() =>
                                      onTogglePreset(preset.id)
                                    }
                                    disabled={readonly || disableEdits}
                                    title={
                                      readonly
                                        ? "Edit lighting presets to change this"
                                        : undefined
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="min-w-0 text-left align-middle">
                                <div className="min-w-0 max-w-full">
                                  <p className="text-sm font-medium truncate text-left">
                                    {preset.name}
                                  </p>
                                </div>
                              </TableCell>
                              <TableCell className="hidden sm:table-cell min-w-0 text-sm text-left align-middle">
                                {selected ? (
                                  <span
                                    className={
                                      rel?.alias?.trim()
                                        ? "text-foreground truncate block text-left"
                                        : "text-muted-foreground"
                                    }
                                    title={
                                      rel?.alias?.trim()
                                        ? rel.alias.trim()
                                        : undefined
                                    }
                                  >
                                    {aliasText}
                                  </span>
                                ) : null}
                              </TableCell>
                              <TableCell className="hidden sm:table-cell text-sm text-muted-foreground align-middle">
                                {selected ? (
                                  preset.address !== null &&
                                  preset.address !== undefined ? (
                                    preset.address
                                  ) : (
                                    "—"
                                  )
                                ) : null}
                              </TableCell>
                              <TableCell className="hidden md:table-cell p-2 align-middle text-center">
                                {selected ? (
                                  <div className="flex justify-center">
                                    {!rel?.lightPresetGroupId ? (
                                      <span className="text-xs text-muted-foreground">
                                        —
                                      </span>
                                    ) : (
                                      <Checkbox
                                        checked={
                                          rel.overridePresetAvailableTimes === true
                                        }
                                        disabled={true}
                                        onCheckedChange={(v) => {
                                          if (
                                            !canEditOverrideCheckbox ||
                                            readonly ||
                                            !rel.lightPresetGroupId
                                          ) {
                                            return;
                                          }
                                          patchOverrideMutation.mutate({
                                            lightPresetGroupId:
                                              rel.lightPresetGroupId,
                                            overridePresetAvailableTimes:
                                              v === true,
                                          });
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label="Override preset schedule with group availability"
                                        title={
                                          readonly
                                            ? "Use group weekly windows instead of the preset’s schedule"
                                            : canLoadSchedule
                                              ? "Use group weekly windows instead of the preset’s schedule"
                                              : "Only site admins can change this"
                                        }
                                      />
                                    )}
                                  </div>
                                ) : null}
                              </TableCell>
                              <TableCell className="hidden md:table-cell p-2 text-xs text-muted-foreground align-middle text-left">
                                {selected ? (
                                  <div className="flex flex-col gap-0.5 items-center">
                                    {rel?.lightPresetGroupId != null &&
                                    rel.overridePresetAvailableTimes !== true ? (
                                      <span
                                        className="text-[10px] font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-400"
                                        title="Using this preset’s weekly windows (override off)"
                                      >
                                        Preset
                                      </span>
                                    ) : null}
                                    <LightPresetAvailabilityCell
                                      canLoadSchedule={canLoadSchedule}
                                      availability={
                                        avail?.state === "pending"
                                          ? { state: "pending" }
                                          : avail?.state === "error"
                                            ? { state: "error" }
                                            : avail?.state === "ok"
                                              ? { state: "ok", rows: avail.rows }
                                              : { state: "pending" }
                                      }
                                      siteCurfew={siteCurfew}
                                      areaCurfew={areaCurfew}
                                    />
                                  </div>
                                ) : null}
                              </TableCell>
                              {showPencil && (
                                <TableCell className="p-2 text-center align-middle">
                                  {selected ? (
                                    <div className="flex justify-center">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-9 w-9"
                                        disabled={
                                          readonly
                                            ? !rel?.lightPresetGroupId
                                            : false
                                        }
                                        onClick={() => {
                                          if (!rel?.lightPresetGroupId) {
                                            props.onUiAlert({
                                              title:
                                                "Availability editor unavailable",
                                              message: readonly
                                                ? "This preset ↔ group link isn’t available yet. Try refreshing the page."
                                                : "Create the group first to create the preset ↔ group link, then you can edit availability for it.",
                                            });
                                            return;
                                          }
                                          if (props.variant === "edit") {
                                            props.onRequestClose();
                                          }
                                          props.onOpenAvailabilityEditor({
                                            preset,
                                            lightPresetGroupId:
                                              rel.lightPresetGroupId,
                                          });
                                        }}
                                        aria-label="Edit availability"
                                        title="Edit availability"
                                      >
                                        <Pencil className="h-4 w-4" aria-hidden />
                                      </Button>
                                    </div>
                                  ) : null}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        },
      )}
    </Accordion>
  );
}

export default function GroupLightingPresetsList(
  props: GroupLightingPresetsListProps,
) {
  const { variant, cardClassName } = props;

  const cardBase = cardClassName ?? "p-4";

  const body = <EditBody {...props} />;

  return (
    <>
      <Card className={cardBase}>{body}</Card>
      {variant === "edit" && (
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={props.onRequestClose}
            disabled={props.isSubmitting}
          >
            {props.isEditingGroup ? "Close" : "Cancel"}
          </Button>
          {!props.isEditingGroup && (
            <Button
              type="button"
              disabled={props.isSubmitting || !props.siteId}
              onClick={props.onApplySelectionForCreate}
            >
              Apply selection
            </Button>
          )}
        </div>
      )}
    </>
  );
}
